import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload, SagaPayloadwithoutRequestData } from '../api.types';
import {
  ActivateProgrammeRequest,
  ActivateProgrammeResponse,
  CreateProgrammeRequest,
  CreateProgrammeResponse,
  DownloadTeamLeaderboardExcelRequest,
  DownloadTeamLeaderboardExcelResponse,
  SendMemberActivationCommunicationsRequest,
  SendMemberActivationCommunicationsResponse,
  UpdateProgrammeRequest,
  UpdateProgrammeResponse,
  UploadMemberExcelRequest,
  UploadMemberExcelResponse,
  ViewAllProgrammesRequest,
  ViewAllProgrammesResponse,
  ViewProgrammeRequest,
  ViewProgrammeResponse
} from './programme.types';

import {
  CREATING_PROGRAMME,
  DOWNLOADING_TEAM_LEADERBOARD_EXCEL,
  PUBLISHING_PROGRAMME,
  RETRIEVE_ALL_PROGRAMMES,
  RETRIEVE_PROGRAMME,
  SENDING_MEMBER_COMMUNICATIONS,
  UPDATING_PROGRAMME,
  UPLOADING_MEMBER_EXCEL
} from './programme.constants';

import { requestViewAllMembers } from '../member/member.actions';
import {
  requestActivateProgramme,
  requestCreateProgramme,
  requestDownloadTeamLeaderboardExcel,
  requestSendMemberActivationCommunications,
  requestUpdateProgramme,
  requestUploadMemberExcel,
  requestViewAllProgrammes,
  requestViewProgramme
} from './programme.actions';

import {
  getActivateProgrammeRequest,
  getCreateProgrammeRequest,
  getDownloadTeamLeaderboardExcelRequest,
  getSendMemberActivationCommunicationsRequest,
  getUpdateProgrammeRequest,
  getUploadMemberExcelRequest,
  getViewAllProgrammesRequest,
  getViewProgrammeRequest
} from './programme.endpoints';

import { setProgramme, setProgrammes } from '../../slices/programme/programme.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR, SUCCESS } from '../../utils/constants';
import { downloadExcelFile } from '../../utils/util';

export function* performCreateProgrammeRequest({ payload }: SagaPayload<CreateProgrammeRequest, string | undefined>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(CREATING_PROGRAMME));

    const [endpoint, options] = getCreateProgrammeRequest(requestData);

    const { data }: AxiosResponse<CreateProgrammeResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data.programmes[0]?.externalReference);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewAllProgrammes({}));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(CREATING_PROGRAMME));
  }
}

export function* watchForCreateProgrammeRequest() {
  yield takeLatest(requestCreateProgramme.type, performCreateProgrammeRequest);
}

export function* performViewAllProgrammesRequest({ payload }: SagaPayloadwithoutRequestData<ViewAllProgrammesRequest>) {
  const { onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVE_ALL_PROGRAMMES));

    const [endpoint, options] = getViewAllProgrammesRequest();

    const { data }: AxiosResponse<ViewAllProgrammesResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(setProgrammes(data.programmes));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVE_ALL_PROGRAMMES));
  }
}

export function* watchForViewAllProgrammesRequest() {
  yield takeLatest(requestViewAllProgrammes.type, performViewAllProgrammesRequest);
}

export function* performViewProgrammeRequest({ payload }: SagaPayload<ViewProgrammeRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVE_PROGRAMME));

    const [endpoint, options] = getViewProgrammeRequest(requestData);

    const { data }: AxiosResponse<ViewProgrammeResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setProgramme(data.programmes[0]));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVE_PROGRAMME));
  }
}

export function* watchForViewProgrammeRequest() {
  yield takeLatest(requestViewProgramme.type, performViewProgrammeRequest);
}

export function* performUpdateProgrammeRequest({ payload }: SagaPayload<UpdateProgrammeRequest, string | undefined>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_PROGRAMME));

    const [endpoint, options] = getUpdateProgrammeRequest(requestData);

    const { data }: AxiosResponse<UpdateProgrammeResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data.programmes[0]?.externalReference);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewAllProgrammes({}));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(UPDATING_PROGRAMME));
  }
}

export function* watchForUpdateProgrammeRequest() {
  yield takeLatest(requestUpdateProgramme.type, performUpdateProgrammeRequest);
}

export function* performActivateProgrammeRequest({ payload }: SagaPayload<ActivateProgrammeRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(PUBLISHING_PROGRAMME));

    const [endpoint, options] = getActivateProgrammeRequest(requestData);

    const { data }: AxiosResponse<ActivateProgrammeResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(requestViewAllProgrammes({}));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(PUBLISHING_PROGRAMME));
  }
}

export function* watchForActivateProgrammeRequest() {
  yield takeLatest(requestActivateProgramme.type, performActivateProgrammeRequest);
}

export function* performDownloadTeamLeaderboardExcel({ payload }: SagaPayload<DownloadTeamLeaderboardExcelRequest>) {
  const { requestData } = payload;

  try {
    yield put(addSpinner(DOWNLOADING_TEAM_LEADERBOARD_EXCEL));

    const request = getDownloadTeamLeaderboardExcelRequest(requestData);

    const { data }: AxiosResponse<DownloadTeamLeaderboardExcelResponse> = yield call(axios, ...request);

    yield call(downloadExcelFile, data.base64EncodedString, `${requestData.programmeName} - Team Leaderboard.xlsx`);

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(DOWNLOADING_TEAM_LEADERBOARD_EXCEL));
  }
}

export function* watchForDownloadTeamLeaderboardExcel() {
  yield takeLatest(requestDownloadTeamLeaderboardExcel.type, performDownloadTeamLeaderboardExcel);
}

export function* performSendMemberActivationCommunicationsRequest({
  payload
}: SagaPayload<SendMemberActivationCommunicationsRequest>) {
  const { requestData } = payload;

  try {
    yield put(addSpinner(SENDING_MEMBER_COMMUNICATIONS));

    const request = getSendMemberActivationCommunicationsRequest(requestData);

    const { data }: AxiosResponse<SendMemberActivationCommunicationsResponse> = yield call(axios, ...request);

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(SENDING_MEMBER_COMMUNICATIONS));
  }
}

export function* watchForSendMemberActivationCommunicationsRequest() {
  yield takeLatest(requestSendMemberActivationCommunications.type, performSendMemberActivationCommunicationsRequest);
}

export function* performUploadMemberExcelRequest({ payload }: SagaPayload<UploadMemberExcelRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPLOADING_MEMBER_EXCEL));

    const [endpoint, options] = getUploadMemberExcelRequest(requestData);

    const { data }: AxiosResponse<UploadMemberExcelResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(
      requestViewAllMembers({ requestData: { programmeExternalReference: requestData.programmeExternalReference } })
    );
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(UPLOADING_MEMBER_EXCEL));
  }
}

export function* watchForUploadMemberExcelRequest() {
  yield takeLatest(requestUploadMemberExcel.type, performUploadMemberExcelRequest);
}

export default function* programmeSaga() {
  yield all([
    watchForCreateProgrammeRequest(),
    watchForViewAllProgrammesRequest(),
    watchForViewProgrammeRequest(),
    watchForUpdateProgrammeRequest(),
    watchForActivateProgrammeRequest(),
    watchForDownloadTeamLeaderboardExcel(),
    watchForSendMemberActivationCommunicationsRequest(),
    watchForUploadMemberExcelRequest()
  ]);
}
